<template>
  <div class="overview">
    <template v-if="workspaceAccess && checklistsOverview && checklists && checklists.length !== 0">
      <div class="overview__header">
        <h2>Your workspace overview</h2>
        <div class="actions">
          <a
            :href="viewAllChecklistsUrl"
            target="_blank"
            class="button button__secondary button-link"
            >View all checklists
          </a>
          <a
            :href="createChecklistsUrl"
            target="_blank"
            class="button button__primary button-link"
            >Create a new checklist
          </a>
        </div>
      </div>
      <div class="overview__content">
        <template v-if="checklistsOverview">
          <ChecklistStatCard
            :label="'Checklists'"
            :value="checklistsOverview.checklists"
            :icon="'clipboard-regular'"
          />
          <ChecklistStatCard
            :label="'Recent activity'"
            :value="checklistsOverview.recentActivity"
            :icon="'comments-regular'"
          />
          <ChecklistStatCard
            :label="'Tasks overdue'"
            :value="checklistsOverview.tasksOverdue"
            :icon="'circle-exclamation-solid'"
          />
          <ChecklistStatCard
            :label="'Tasks awaiting responses'"
            :value="checklistsOverview.tasksAwaiting"
            :icon="'circle-question-regular'"
          />
          <ChecklistStatCard
            :label="'Tasks blocked'"
            :value="checklistsOverview.tasksBlocked"
            :icon="'ban-solid'"
          />
        </template>
      </div>
    </template>
    <div v-else class="get-started">
      <div class="get-started__image"></div>
      <div class="get-started__content">
        <h2>Vixio Workspace</h2>
        <p>
          Start here to discover how Vixio Workspace can transform your
          regulatory intelligence into actionable tasks. Use a single workspace
          to create checklists, assign tasks, track progress, capture
          attestation and stay effortlessly on top of everything.
        </p>
        <div class="actions">
          <div class="tooltip-assist">
            <a
              v-if="workspaceAccess"
              :href="createChecklistsUrl"
              class="button button__primary start-here button-link"
              target="_blank"
              >Start here
            </a>
            <el-button
              v-else
              @click="openDemoLink"
              class="button button__primary start-here button-link"
              >Book a demo
            </el-button>
            <!-- <Tooltip :size="'small'" class="tip" /> -->
          </div>
          <el-button class="button button__secondary" @click="showHowItWorks"
            >How it works
          </el-button>
        </div>
      </div>
    </div>
    <checklist-video-embed-modal
      v-model="howItWorksVisible"
    ></checklist-video-embed-modal>
  </div>
</template>
<script>
import { mapGetters, mapState } from "vuex";
import ChecklistStatCard from "~/components/checklists/ChecklistStatCard.vue";
// import Tooltip from "~/components/elements/Tooltip.vue";
import ChecklistVideoEmbedModal from "~/components/checklists/ChecklistVideoEmbedModal.vue";

export default {
  name: "ChecklistOverview",
  components: { ChecklistVideoEmbedModal, /* Tooltip, */ ChecklistStatCard },
  data() {
    return {
      howItWorksVisible: false,
    };
  },
  computed: {
    ...mapState("Workspace", ["checklistsOverview", "checklists"]),
    ...mapGetters("Auth", ["workspaceAccess"]),
    viewAllChecklistsUrl() {
      return `${this.$config.workspaceFEURL}`;
    },
    createChecklistsUrl() {
      return `${this.$config.workspaceFEURL}/add-checklist`;
    },
  },
  methods: {
    showHowItWorks() {
      this.howItWorksVisible = true;
    },
    openDemoLink() {
      window.open("https://www.vixio.com/vixio-workspace#apply-now", "_blank");
    },
  },
};
</script>

<style scoped lang="scss">
@import "~assets/styles/helpers/variables";
@import "~assets/styles/helpers/mixin";

.overview {
  width: 100%;
  min-height: 200px;

  .actions {
    display: flex;
    gap: 24px;

    .button {
      height: 44px;
      padding: 10px 18px;
      border: 1px solid $corporateBlue;
      font-weight: 400;
      line-height: 24px;
      font-size: 16px;
      letter-spacing: 0;
      border-radius: 8px;
      box-shadow: 0px 1px 2px 0px #1018280d;
      margin: 0;

      &__primary {
        background-color: $corporateBlue;
        color: $white;
      }

      &__secondary {
        background-color: white;
        color: $corporateBlue;
      }
    }
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 38px;
    flex-wrap: wrap;

    h2 {
      font-weight: 700;
      font-size: 32px;
      line-height: 40px;
      letter-spacing: 0;
      text-align: center;
      text-wrap: nowrap;
    }
  }

  &__content {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    @include below($xl) {
      gap: 12px;
    }
    flex-wrap: wrap;
    padding-bottom: 24px;
    flex-direction: row;

    & > * {
      height: 172px;
      border-radius: 12px;
      flex: 0.3 0 160px;
      @include below($lg) {
        flex-basis: 222px;
        flex-grow: 0.2;
        &:nth-last-child(-n+2) {
          flex-grow: 0.15;
        }
      }
      @include below($md) {
        &:nth-last-child(-n+2) {
          flex-grow: 0.14;
        }
      }
    }
  }

  .get-started {
    display: flex;
    flex-direction: row;
    min-height: 180px;
    border-radius: 12px;
    margin-bottom: 20px;
    border: 1px solid #cfd0d1;

    .get-started__image {
      flex: 0 0 250px;
      background-position: 10px 3px, 10px 15px;
      background-size: contain;
      background-repeat: no-repeat;
      background-image: url("../../assets/images/ws-homepage.png");
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }

    .get-started__content {
      padding: 10px 20px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      color: #262a2e;

      h2 {
        font-weight: 700;
        font-size: 24px;
        line-height: 32px;
        letter-spacing: 0;
        text-align: center;
        text-wrap: nowrap;
        margin-bottom: 4px;
      }

      p {
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0;
        margin-bottom: 20px;
      }
    }

    .tooltip-assist {
      position: relative;
    }

    .start-here[disabled] {
      &:hover {
        & + .tip {
          display: block;
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }

    .tooltip:hover {
      display: block;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .button-link {
    text-decoration: none;
    display: inline-block;
  }
}
</style>
